import { Save01, Trash01, X } from "@untitledui/icons";
import { useEffect, useState } from "react";
import {
  useCreateAnalyticsDashboard,
  useDeleteAnalyticsDashboard,
  useUpdateAnalyticsDashboard,
} from "@/api/analytics-dashboards";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";
import type { AnalyticsView } from "./AnalyticsViewConfigurationModal";
import { AnalyticsViewConfigurationModal } from "./AnalyticsViewConfigurationModal";
import { DashboardContent, type LayoutItem } from "./DashboardContent";

export interface Dashboard {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  views: AnalyticsView[];
  layout?: LayoutItem[];
}

interface DashboardConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete?: (dashboardId: string) => void;
  onSave?: (dashboardId: string) => void;
  existingDashboard?: Dashboard | null;
}

export const DashboardConfigurationModal: React.FC<DashboardConfigurationModalProps> = ({
  isOpen,
  onClose,
  onDelete,
  onSave,
  existingDashboard = null,
}) => {
  const [dashboardName, setDashboardName] = useState(existingDashboard?.name || "");
  const [isAddViewOpen, setIsAddViewOpen] = useState(false);
  const [isEditViewOpen, setIsEditViewOpen] = useState(false);
  const [editingView, setEditingView] = useState<AnalyticsView | null>(null);
  const [views, setViews] = useState<AnalyticsView[]>([]);
  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const isEditing = !!existingDashboard;

  // Database mutation hooks
  const createDashboardMutation = useCreateAnalyticsDashboard();
  const updateDashboardMutation = useUpdateAnalyticsDashboard();
  const deleteDashboardMutation = useDeleteAnalyticsDashboard();

  // Update dashboard name and views when existingDashboard changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingDashboard) {
        setDashboardName(existingDashboard.name);
        setViews(existingDashboard.views || []);
        setLayout(existingDashboard.layout || []);
      } else {
        setDashboardName("");
        setViews([]);
        setLayout([]);
      }
      // Reset validation state when modal opens
      setHasAttemptedSave(false);
    }
  }, [existingDashboard, isOpen]);

  // Track if form has been touched
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Validate form
  const isFormValid = () => {
    return dashboardName.trim().length > 0;
  };

  const hasValidationError = hasAttemptedSave && !dashboardName.trim();

  const handleSave = async () => {
    setHasAttemptedSave(true);
    if (!isFormValid()) return;

    const spec = {
      name: dashboardName,
      views: views.map((v) => ({
        id: v.id,
        name: v.name,
        layout: v.layout,
        metrics: v.metrics,
      })),
      layout: layout,
    };

    let savedDashboardId: string;

    if (isEditing && existingDashboard) {
      // Update existing dashboard
      await updateDashboardMutation.mutateAsync({
        id: existingDashboard.id,
        input: { spec },
      });
      savedDashboardId = existingDashboard.id;
    } else {
      // Create new dashboard
      const result = await createDashboardMutation.mutateAsync({ spec });
      savedDashboardId = result.id;
    }

    // Call onSave callback with the dashboard ID
    if (onSave) {
      onSave(savedDashboardId);
    }

    onClose();
  };

  const handleDelete = async () => {
    if (!existingDashboard) return;

    // Delete from database
    await deleteDashboardMutation.mutateAsync(existingDashboard.id);

    // Call onDelete callback if provided
    if (onDelete) {
      onDelete(existingDashboard.id);
    }

    onClose();
  };

  const handleCancel = () => {
    setDashboardName(existingDashboard?.name || "");
    onClose();
  };

  const handleAddView = (view: AnalyticsView) => {
    // Add new view to the list
    setViews([...views, view]);

    // Add layout item for the new view (12 columns wide, 4 rows high)
    // Stack it below existing views
    const maxY = layout.length > 0 ? Math.max(...layout.map((item) => item.y + item.h)) : 0;
    const newLayoutItem: LayoutItem = {
      i: view.id,
      x: 0,
      y: maxY,
      w: 12,
      h: 4,
    };
    setLayout([...layout, newLayoutItem]);
  };

  const handleEditWidget = (viewId: string) => {
    const view = views.find((v) => v.id === viewId);
    if (view) {
      setEditingView(view);
      setIsEditViewOpen(true);
    }
  };

  const handleUpdateView = (updatedView: AnalyticsView) => {
    // Replace the old view with the updated one
    const updatedViews = views.map((v) => (v.id === updatedView.id ? updatedView : v));
    setViews(updatedViews);
    setEditingView(null);
  };

  const handleDeleteWidget = (viewId: string) => {
    // Remove view from the list
    setViews(views.filter((v) => v.id !== viewId));
    // Remove from layout
    setLayout(layout.filter((item) => item.i !== viewId));
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onClose} isDismissable>
      <Modal className="flex items-center justify-center">
        <Dialog className="w-full max-w-[1400px] h-full max-h-[calc(100vh-68px)] px-16 py-12 bg-white rounded-xl inline-flex flex-col justify-start items-start gap-8 overflow-auto outline-hidden">
          <div className="self-stretch flex flex-col justify-end items-start gap-16">
            <div className="self-stretch inline-flex justify-between items-end">
              {/* Dashboard Name Input */}
              <div className="w-[406px] inline-flex flex-col justify-start items-start gap-1.5">
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                  <div className="inline-flex justify-start items-start gap-0.5">
                    <Label className="text-fg-tertiary_hover text-sm font-medium leading-tight">Dashboard name</Label>
                  </div>
                  <Input
                    type="text"
                    value={dashboardName}
                    onChange={(value) => setDashboardName(value)}
                    placeholder="Enter dashboard name"
                    className={cx("self-stretch", hasValidationError && "ring-2 ring-red-500")}
                  />
                  {hasValidationError && <span className="text-xs text-red-500">Dashboard name is required</span>}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end items-center gap-3 flex-wrap content-center">
                <Button color="secondary" iconLeading={X} onClick={handleCancel}>
                  Cancel
                </Button>

                <Button color="secondary" iconLeading={Trash01} onClick={handleDelete} isDisabled={!isEditing}>
                  Delete
                </Button>

                <Button color="primary" iconLeading={Save01} onClick={handleSave}>
                  Save Dashboard
                </Button>
              </div>
            </div>
          </div>

          <div className="self-stretch flex flex-col justify-center items-center">
            {/* Dashboard Content */}
            <DashboardContent
              views={views}
              editable={true}
              onAddView={() => setIsAddViewOpen(true)}
              layout={layout}
              onLayoutChange={setLayout}
              onEditWidget={handleEditWidget}
              onDeleteWidget={handleDeleteWidget}
              timePeriod="weekly"
              dateRange={null}
              timeGranularity="week"
            />
          </div>

          {/* Add Analytics View Modal */}
          <AnalyticsViewConfigurationModal
            isOpen={isAddViewOpen}
            onOpenChange={setIsAddViewOpen}
            onSave={handleAddView}
            editMode={false}
          />

          {/* Edit Analytics View Modal */}
          <AnalyticsViewConfigurationModal
            isOpen={isEditViewOpen}
            onOpenChange={setIsEditViewOpen}
            onSave={handleUpdateView}
            editMode={true}
            existingView={editingView || undefined}
          />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
